import { getOrderedAccountAddressIds } from './get-ordered-account-address-ids';

/**
 * Rewrites the flat stored order list after a drag of `accountAddressUniqueIds`
 * (the dragged surface's address set — the account's full list in the account
 * selector, or just the active-basket subset in the config strip and the user
 * menu). The dragged ids are PREPENDED in their new order, so the latest
 * dragged subset always reads first for that account; the account's remaining
 * stored entries (addresses not visible on the dragged surface) follow in
 * their old relative order, and entries of other accounts are kept untouched.
 * Ids in the dragged set are stripped from their old slots first, so an id
 * never appears twice.
 *
 * Returns `null` when nothing should be written: the requested order is not a
 * permutation of the dragged set (a stale drag against an already-changed
 * list) or the dragged set's read order is unchanged.
 */
export const setAccountAddressOrder = (
  accountAddressUniqueIds: readonly string[],
  storedOrderedIds: readonly string[] | undefined | null,
  orderedAccountAddressIds: readonly string[],
): string[] | null => {
  const isPermutation =
    orderedAccountAddressIds.length === accountAddressUniqueIds.length &&
    new Set(orderedAccountAddressIds).size ===
      orderedAccountAddressIds.length &&
    orderedAccountAddressIds.every((id) =>
      accountAddressUniqueIds.includes(id),
    );

  const currentOrder = getOrderedAccountAddressIds(
    accountAddressUniqueIds,
    storedOrderedIds,
  );

  if (
    !isPermutation ||
    orderedAccountAddressIds.every((id, index) => id === currentOrder[index])
  ) {
    return null;
  }

  return [
    ...orderedAccountAddressIds,
    ...(storedOrderedIds ?? []).filter(
      (id) => !accountAddressUniqueIds.includes(id),
    ),
  ];
};
